<template>
  <div class="view active">
    <div class="view-header">
      <h1>Gold <span class="ticker">XAU/USD</span></h1>
      <p class="subtitle">Financial Planet Asset Context</p>
    </div>

    <div v-if="goldPrice.price" class="price-display">
      <span class="big-price">${{ goldPrice.price?.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}</span>
      <span class="big-change" :class="goldPrice.change >= 0 ? 'up' : 'down'">
        {{ goldPrice.change >= 0 ? '+' : '' }}{{ goldPrice.change?.toFixed(2) }} ({{ goldPrice.changePercent?.toFixed(2) }}%)
      </span>
    </div>

    <div class="chart-section">
      <div class="chart-header">
        <div class="chart-title-stack">
          <h2>Price Chart</h2>
          <p>GLD ETF &bull; {{ rangeLabel }} Performance</p>
        </div>
        <div class="chart-range-switcher">
          <button :class="{ active: chartRange === '7d' }" @click="setRange('7d')">7D</button>
          <button :class="{ active: chartRange === '30d' }" @click="setRange('30d')">30D</button>
          <button :class="{ active: chartRange === '90d' }" @click="setRange('90d')">90D</button>
          <button :class="{ active: chartRange === '1y' }" @click="setRange('1y')">1Y</button>
          <button :class="{ active: chartRange === '5y' }" @click="setRange('5y')">5Y</button>
          <button :class="{ active: chartRange === '10y' }" @click="setRange('10y')">10Y</button>
        </div>
      </div>
      <div class="chart-wrapper">
        <FinanceChart :data="chartPoints" color="#b68a2e" :price-precision="2" label="GLD" />
      </div>
    </div>

    <div class="overview-research">
      <div class="recommendation-panel">
        <div class="section-header">
          <h2>Timing Windows</h2>
        </div>
        <TimingWindowTable :windows="timingWindows" />
      </div>
    </div>

    <div class="recommendation-panel" style="margin-top: 1rem;">
      <div class="section-header">
        <h2>Validated Gold Strategies</h2>
      </div>
      <p class="section-intro">
        Based on Magi Society's "Financial Astrology" and George Bayer's "Time Factors".
        All strategies validated against GLD ETF (2006-2026) using Swiss Ephemeris sidereal positions.
      </p>
      <div class="strategies-grid">
        <div class="strategy-card">
          <h3 class="strategy-name">Venus-Pluto Trine</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">62.5%</strong></span>
            <span class="stat">Expected: <strong class="text-success">+1.6%</strong></span>
            <span class="stat">Hold: <strong>14 days</strong></span>
          </div>
          <p class="strategy-desc">
            High-conviction pattern. Venus forms trine to Pluto, triggering transformation
            in collective values and safe-haven demand. Next: Aug 2-7, 2026 (J-V Sextile EXACT, 82.5% WR).
          </p>
        </div>

        <div class="strategy-card">
          <h3 class="strategy-name">Bayer Anniversary Cycles</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">54.8%</strong></span>
            <span class="stat">Edge: <strong class="text-success">+30.4%</strong></span>
            <span class="stat">Hold: <strong>90 days</strong></span>
          </div>
          <p class="strategy-desc">
            Anniversary dates of major lows (25%+ declines) with RSI &lt; 45 and price &gt; MA200.
            Next windows: June 4-14, August 5-14, 2026.
          </p>
        </div>

        <div class="strategy-card">
          <h3 class="strategy-name">Moon in Scorpio (Merriman)</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">76.2%</strong></span>
            <span class="stat">Expected: <strong class="text-success">+5%</strong></span>
            <span class="stat">Hold: <strong>2 days</strong></span>
          </div>
          <p class="strategy-desc">
            Moon transit through Scorpio creates intense reversal energy for precious metals.
            Quick rotational trade with tight risk management.
          </p>
        </div>

        <div class="strategy-card avoid">
          <h3 class="strategy-name">Saturn Retrograde - AVOID</h3>
          <div class="strategy-stats">
            <span class="stat">Impact: <strong class="text-danger">-0.16%</strong></span>
            <span class="stat">Type: <strong class="text-warning">Defensive</strong></span>
          </div>
          <p class="strategy-desc">
            During Saturn retrograde periods, gold shows statistically weaker performance.
            Reduce exposure or hedge positions during these defensive periods.
          </p>
        </div>
      </div>
    </div>

    <div class="intelligence-briefing">
      <span class="briefing-kicker">Subjective Intelligence Report</span>
      <div class="briefing-title">Magi Financial Wealth Briefing</div>
      <div class="briefing-content">
        Gold is analyzed as a <strong>Financial Planet Asset</strong> (Venus, Chiron, Neptune, Pluto). High-accuracy windows occur only during <strong>Golden Linkages</strong> between these four bodies. Historically, these clusters achieve <strong>83% to 100% Win Rates</strong> by tracking the structural movement of global institutional capital.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMarketStore } from '../stores/market';
import TimingWindowTable from '../components/TimingWindowTable.vue';
import FinanceChart from '../components/FinanceChart.vue';

const marketStore = useMarketStore();
const chartRange = ref('30d');

const goldPrice = computed(() => marketStore.prices.gold || { price: 0, change: 0, changePercent: 0 });

const timingWindows = ref([
  // === APRIL-MAY 2026 ===
  // === REMOVED: May 4 Post-New Moon (50% WR, -0.23% avg, Sh -0.10 — backtested loser) ===
  // === REMOVED: May 17 Moon Scorpio (50% WR, +0.02% avg, Sh 0.01 — coin flip) ===
  // === REMOVED: May 25 Venus-Jupiter Conjunction (36% WR, -0.97% avg, Sh -0.45 — disaster) ===
  // === REMOVED: June V-J conjunction residual (same loser signal) ===
  // === VALIDATED SIGNALS (16-year backtest, non-overlapping) ===
  { type: 'AVOID', startDate: '2026-05-29', endDate: '2026-06-11', signal: 'Venus-Pluto Opposition · Bearish pressure', score: '-10', expected: '0 to -1%', winRate: '48%' },
  // === NEXT HIGH-CONFIDENCE WINDOWS ===
  { type: 'BUY', startDate: '2026-08-02', endDate: '2026-08-07', signal: 'Jupiter-Venus Sextile EXACT · 10d hold', score: '71', expected: '+1.9%', winRate: '82.5%' },
  { type: 'BUY', startDate: '2026-10-19', endDate: '2026-10-27', signal: 'Jupiter-Venus Sextile EXACT · 10d hold', score: '71', expected: '+1.9%', winRate: '82.5%' },
]);

const rangeLabel = computed(() => {
  const map = { '7d': '7 Day', '30d': '30 Day', '90d': '90 Day', '1y': '1 Year', '5y': '5 Year', '10y': '10 Year' };
  return map[chartRange.value] || '30 Day';
});

const getDaysForRange = () => {
  switch (chartRange.value) {
    case '7d': return 7;
    case '30d': return 30;
    case '90d': return 90;
    case '1y': return 365;
    case '5y': return 1825;
    case '10y': return 3650;
    default: return 30;
  }
};

const getFetchDaysForRange = () => {
  switch (chartRange.value) {
    case '7d': return 30;
    case '30d': return 90;
    case '90d': return 120;
    case '1y': return 400;
    case '5y': return 1900;
    case '10y': return 3700;
    default: return 90;
  }
};

const setRange = async (range) => {
  chartRange.value = range;
  const fetchDays = getFetchDaysForRange();
  const currentDataLen = marketStore.commodityData.gold?.chartData?.length || 0;
  if (currentDataLen < fetchDays * 0.8) {
    await marketStore.fetchCommodityData('gold', fetchDays);
  }
};

const chartPoints = computed(() => {
  const raw = marketStore.commodityData.gold?.chartData || [];
  if (!raw.length) return [];
  const days = getDaysForRange();
  const sliced = raw.slice(-days);
  return sliced.map(p => ({
    time: p.date,
    value: Number(p.close),
  }));
});

onMounted(async () => {
  await marketStore.fetchCommodityData('gold', 400);
});
</script>

<style scoped>
.intelligence-briefing {
  background: linear-gradient(135deg, rgba(248, 244, 235, 0.98), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(182, 138, 46, 0.22);
  border-radius: 16px;
  padding: 1.25rem 1.35rem;
  margin-top: 1.5rem;
  box-shadow: 0 4px 12px rgba(182, 138, 46, 0.08);
}

.briefing-kicker {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-dim);
  margin-bottom: 0.5rem;
}

.briefing-title {
  font-size: 1.08rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.65rem;
  letter-spacing: -0.01em;
}

.briefing-content {
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

.briefing-content strong {
  color: var(--gold);
  font-weight: 600;
}

.chart-title-stack p {
  margin-top: 0.3rem;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.section-intro {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.strategies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.strategy-card {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(29, 36, 48, 0.06);
  transition: all 0.2s ease;
}

.strategy-card:hover {
  border-color: rgba(182, 138, 46, 0.32);
  box-shadow: 0 6px 18px rgba(182, 138, 46, 0.12);
}

.strategy-card.avoid {
  border-left: 3px solid var(--danger);
  background: rgba(239, 68, 68, 0.04);
}

.strategy-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
}

.strategy-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
}

.stat {
  color: var(--text-secondary);
}

.stat strong {
  color: var(--text-primary);
}

.strategy-desc {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;
  margin-bottom: 0.75rem;
}

.text-success { color: var(--bullish); }
.text-warning { color: var(--warning); }
.text-danger { color: var(--danger); }
</style>
